<script lang="ts">import { t, Verbiage } from "@infinitro/content";
import { Text } from "@infinitro/text";
import { onMount } from "svelte";
import { getGameReviewDistribution } from "../../api";
export let game = undefined;
let averageRating = 0;
let totalRatings = 0;
let ratingDistribution = [];
let isLoading = true;
async function loadRatingDistribution() {
	if (!game || !game.game_slug || !game.provider_slug) return;
	isLoading = true;
	try {
		const distribution = await getGameReviewDistribution(game.game_slug, game.provider_slug);
		if (!distribution || distribution.length === 0) {
			// No reviews yet
			averageRating = 0;
			totalRatings = 0;
			ratingDistribution = [
				{
					stars: 5,
					percentage: 0
				},
				{
					stars: 4,
					percentage: 0
				},
				{
					stars: 3,
					percentage: 0
				},
				{
					stars: 2,
					percentage: 0
				},
				{
					stars: 1,
					percentage: 0
				}
			];
		} else {
			// Backend returns starRating in 0-10 scale, need to convert to 1-5 stars
			// Group by star buckets (1-5 scale)
			const grouped = {
				5: {
					count: 0,
					percentage: 0
				},
				4: {
					count: 0,
					percentage: 0
				},
				3: {
					count: 0,
					percentage: 0
				},
				2: {
					count: 0,
					percentage: 0
				},
				1: {
					count: 0,
					percentage: 0
				}
			};
			let totalReviewCount = 0;
			let weightedSum = 0;
			distribution.forEach((item) => {
				const rating = parseFloat(item.starRating);
				const count = item.reviewCount;
				const percentage = parseFloat(item.percentage);
				// Backend returns starRating already in 0-5 scale (5, 4.5, 4, 3.5, etc.)
				// Round to nearest integer to get star bucket (1-5)
				const stars = Math.max(1, Math.min(5, Math.round(rating)));
				grouped[stars].count += count;
				grouped[stars].percentage += percentage;
				totalReviewCount += count;
				weightedSum += rating * count;
			});
			totalRatings = totalReviewCount;
			// Backend already returns ratings in 0-5 scale, no need to divide by 2
			averageRating = totalReviewCount > 0 ? weightedSum / totalReviewCount : 0;
			// Build final distribution array
			ratingDistribution = [
				5,
				4,
				3,
				2,
				1
			].map((stars) => ({
				stars,
				percentage: Math.round(grouped[stars].percentage)
			}));
		}
	} catch (error) {
		console.warn("Error fetching review distribution:", error);
	} finally {
		isLoading = false;
	}
}
// Load on mount
onMount(() => {
	loadRatingDistribution();
});
</script>

<div class="average-rating-container">
	{#if isLoading}
		<div class="loading-state">
			<Text type="body" bodySize="medium">Loading ratings...</Text>
		</div>
	{:else if totalRatings === 0}
		<div class="no-ratings">
			<Text type="body" bodySize="medium" customStyle="color: var(--color-neutral-500);"
				>{$t(Verbiage.no_ratings_yet)}</Text
			>
		</div>
	{:else}
		<div class="rating-summary">
			<div class="rating-score">
				<Text type="body" bodySize="large" bodyWeight="semibold"
					>{$t(Verbiage.reviews_average_rating)}</Text
				>

				<div class="score-and-stars">
					<Text type="title" titleSize="h1" customStyle="font-size: 1.5rem; line-height: 1;"
						>{averageRating.toFixed(1)}</Text
					>
					<div class="stars">
						{#each [1, 2, 3, 4, 5] as star}
							<svg
								class="summary-star"
								class:summary-star--filled={star <= Math.round(averageRating)}
								width="20"
								height="20"
								viewBox="0 0 24 24"
								stroke-width="2"
							>
								<path
									d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
								/>
							</svg>
						{/each}
					</div>
				</div>
				<Text type="body" bodySize="small" textColor="gray"
					>{$t(Verbiage.reviews_based_on_user_ratings, 'global', { count: totalRatings })}</Text
				>
			</div>

			<div class="rating-distribution">
				{#each ratingDistribution as rating}
					<div class="rating-bar-row">
						<div class="star-number">
							<Text type="body" bodySize="small">{rating.stars}</Text>
							<svg
								class="distribution-star"
								width="14"
								height="14"
								viewBox="0 0 24 24"
								stroke-width="2"
							>
								<path
									d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
								/>
							</svg>
						</div>
						<div class="rating-bar-container">
							<div class="rating-bar" style="width: {rating.percentage}%"></div>
						</div>
						<Text type="body" bodySize="small">{rating.percentage}%</Text>
					</div>
				{/each}
			</div>
		</div>
	{/if}
</div>

<style>
	.average-rating-container {
		display: flex;
		flex-direction: column;
		border-radius: 0.5rem;
		padding: 1rem;
		background: transparent;
		gap: 1rem;
}

	.rating-summary {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		justify-content: flex-start;
		gap: 1.5rem;
}

	@media (min-width: 768px) {

		.rating-summary {
				flex-direction: row;
		}
}

	.rating-score {
		display: flex;
		flex: 1 1 0%;
		flex-direction: column;
		align-items: flex-start;
		gap: 0.5rem;
}

	.score-and-stars {
		display: flex;
		align-items: center;
		gap: 0.75rem;
}

	.stars {
		display: flex;
		gap: 0.25rem;
}

	.rating-distribution {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		width: 30%;
}

	.rating-bar-row {
		display: flex;
		align-items: center;
		gap: 0.75rem;
}

	.star-number {
		display: flex;
		align-items: center;
		gap: 0.25rem;
}

	.rating-bar-container {
		height: 0.5rem;
		flex: 1 1 0%;
		border-radius: 9999px;
		background-color: var(--color-neutral-900);
}

	.rating-bar {
		height: 100%;
		border-radius: 9999px;
		background: var(--color-primary-500);
}

	.summary-star {
		fill: var(--color-neutral-100);
		stroke: var(--color-neutral-100);
	}

	.summary-star--filled {
		fill: var(--color-primary-500);
		stroke: var(--color-primary-500);
		filter: drop-shadow(0 0 4px color-mix(in srgb, var(--color-primary-500) 60%, transparent));
	}

	.distribution-star {
		fill: var(--color-primary-500);
		stroke: var(--color-primary-500);
	}

	.loading-state,
	.no-ratings {
		display: flex;
		align-items: center;
		justify-content: center;
		padding-top: 2rem;
		padding-bottom: 2rem;
}

	@media (max-width: 768px) {
		.rating-distribution {
			width: 100%;
		}
	}
</style>
